import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { updateProfile, createJob, getCountries, getUserJobs, getImageUrl, getUserPublicProfile, getUserReviews, deleteJob } from '../services/api';
import './Profile.css';
import ImageCropper from '../components/ImageCropper';

const Profile = ({ language }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('ads');
    const [user, setUser] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [updateSuccess, setUpdateSuccess] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [countries, setCountries] = useState([]);
    const [myJobs, setMyJobs] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [loadingReviews, setLoadingReviews] = useState(false);

    // Profile Edit State
    const [editName, setEditName] = useState('');
    const [avatarFile, setAvatarFile] = useState(null);
    const [bannerFile, setBannerFile] = useState(null);
    const [croppingImage, setCroppingImage] = useState(null); // URL of image to crop

    // New Ad State
    const [newAd, setNewAd] = useState({
        name: '',
        country: [], // Changed to array for multiselect
        preview_text: '',
        detail_text: '',
        price: '',
        type: 'Агентства',
        ad_type: 'OFFERING',
        image: null
    });

    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);

    const loadUserJobs = async () => {
        try {
            const res = await getUserJobs();
            setMyJobs(res.data || []);
        } catch (err) {
            console.error('Error loading my jobs:', err);
        }
    };

    const loadUserData = async (id) => {
        try {
            const res = await getUserPublicProfile(id);
            if (res.data) {
                setUser(res.data);
                setEditName(res.data.name || res.data.login || '');
                // Sync with local storage just in case
                localStorage.setItem('user', JSON.stringify(res.data));
            }
        } catch (err) {
            console.error('Error loading fresh user data:', err);
        }
    };

    const loadReviews = async (id) => {
        try {
            setLoadingReviews(true);
            const res = await getUserReviews(id);
            setReviews(res.data || []);
        } catch (err) {
            console.error('Error loading reviews:', err);
        } finally {
            setLoadingReviews(false);
        }
    };

    const StarRating = ({ rating }) => {
        return (
            <div className="star-rating" style={{ display: 'flex', gap: '5px' }}>
                {[1, 2, 3, 4, 5].map((star) => (
                    <span
                        key={star}
                        style={{
                            fontSize: '18px',
                            color: star <= Math.round(rating) ? '#9b59b6' : '#ccc'
                        }}
                    >
                        {star <= Math.round(rating) ? '★' : '☆'}
                    </span>
                ))}
            </div>
        );
    };

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('token');
        if (!storedUser || !token) {
            navigate('/auth');
            return;
        }
        const parsedUser = JSON.parse(storedUser);
        if (!parsedUser) {
            navigate('/auth');
            return;
        }

        // Initial setup from local storage
        setUser(parsedUser);
        setEditName(parsedUser.name || parsedUser.login || '');

        // Fetch fresh data and reviews
        loadUserData(parsedUser.id);
        loadReviews(parsedUser.id);

        // Fetch countries for dropdown
        getCountries().then(res => setCountries(res.data || []));

        // Load jobs
        loadUserJobs();

        const params = new URLSearchParams(location.search);
        if (params.get('tab') === 'ads') {
            setActiveTab('ads');
        }
    }, [location, navigate]);

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('name', editName);
        if (avatarFile) formData.append('avatar', avatarFile);
        if (bannerFile) formData.append('banner', bannerFile);

        try {
            const res = await updateProfile(formData);
            if (res.data.success) {
                const refreshedUser = res.data.user;
                localStorage.setItem('user', JSON.stringify(refreshedUser));
                setUser(refreshedUser);
                // Trigger header sync
                window.dispatchEvent(new Event('authChange'));

                setIsEditing(false);
                setBannerFile(null); // Clear pending files
                setAvatarFile(null);
                setUpdateSuccess(true);
                setTimeout(() => setUpdateSuccess(false), 3000);
            }
        } catch (err) {
            alert('Ошибка при обновлении профиля');
        }
    };

    const handleBannerFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            setBannerFile(file);
            // We don't read as dataURL here anymore because we use URL.createObjectURL in render
            // Just reset the input
            e.target.value = null;
        }
    };

    const handleCropSave = (croppedBlob) => {
        // Convert blob to file for upload
        const file = new File([croppedBlob], "banner.jpg", { type: "image/jpeg" });
        setBannerFile(file);
        setCroppingImage(null);
    };

    const handleAdSubmit = async (e) => {
        e.preventDefault();
        try {
            // Prepare FormData: Handle country array
            const formData = new FormData();
            Object.keys(newAd).forEach(key => {
                if (key === 'country') {
                    // Send as JSON string for array
                    formData.append(key, JSON.stringify(newAd.country));
                } else {
                    formData.append(key, newAd[key]);
                }
            });

            const res = await createJob(formData);
            if (res.data.success) {
                // Success! Close form modal and open success modal
                setIsModalOpen(false);
                setIsSuccessModalOpen(true);

                // Close success modal after 2 seconds automatically
                setTimeout(() => setIsSuccessModalOpen(false), 2000);

                setNewAd({
                    name: '', country: [], preview_text: '', detail_text: '', price: '', type: 'Агентства', image: null
                });
                loadUserJobs();
            }
        } catch (err) {
            alert('Ошибка при создании объявления');
        }
    };

    const handleDeleteJob = async (jobId) => {
        if (window.confirm('Вы уверены, что хотите удалить это объявление?')) {
            try {
                const res = await deleteJob(jobId);
                if (res.data.success) {
                    loadUserJobs();
                }
            } catch (err) {
                console.error('Error deleting job:', err);
                alert('Ошибка при удалении объявления');
            }
        }
    };

    if (!user) return null;

    const cleanText = (text) => {
        if (!text) return '';
        return String(text)
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, '&');
    };

    return (
        <div className="profile-page">
            {croppingImage && (
                <ImageCropper
                    imageSrc={croppingImage}
                    onCropComplete={handleCropSave}
                    onCancel={() => setCroppingImage(null)}
                />
            )}
            <div className="profile-header">
                <div className="profile-banner" style={{ backgroundImage: bannerFile ? `url(${URL.createObjectURL(bannerFile)})` : (user.banner ? `url(${getImageUrl(user.banner)})` : 'none') }}>
                    {isEditing && (
                        <label className="banner-upload">
                            <span>Сменить фон</span>
                            <input type="file" hidden onChange={handleBannerFileChange} accept="image/*" />
                        </label>
                    )}
                </div>
                <div className="container">
                    <div className="profile-info-card">
                        <div className="profile-avatar-wrapper">
                            <div className="profile-avatar">
                                {avatarFile ? (
                                    <img src={URL.createObjectURL(avatarFile)} alt="preview" />
                                ) : user.avatar ? (
                                    <img src={getImageUrl(user.avatar)} alt="avatar" />
                                ) : (
                                    (user.name || user.NAME) ? (user.name || user.NAME).charAt(0).toUpperCase() : (user.login || user.LOGIN).charAt(0).toUpperCase()
                                )}
                            </div>
                            {isEditing && (
                                <label className="avatar-upload">
                                    <i className="edit-icon">📷</i>
                                    <input type="file" hidden onChange={e => {
                                        if (e.target.files && e.target.files[0]) {
                                            setAvatarFile(e.target.files[0]);
                                        }
                                    }} accept="image/*" />
                                </label>
                            )}
                        </div>

                        <div className="profile-details">
                            {isEditing ? (
                                <div className="edit-fields">
                                    <input
                                        type="text"
                                        value={editName}
                                        onChange={e => setEditName(e.target.value)}
                                        placeholder="Ваше имя"
                                    />
                                    <div className="edit-actions">
                                        <button className="btn-save" onClick={handleProfileUpdate}>Сохранить</button>
                                        <button className="btn-cancel" onClick={() => setIsEditing(false)}>Отмена</button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="name-rating-wrapper">
                                        <h1 style={{ margin: 0 }}>{user.name || user.NAME || user.login || user.LOGIN}</h1>
                                        {user.total_reviews > 0 && (
                                            <div className="profile-avg-rating">
                                                <StarRating rating={user.avg_rating} />
                                                <span className="rating-number">
                                                    {Number(user.avg_rating).toFixed(1)} ({user.total_reviews})
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <p className="profile-meta">Пользователь с {new Date(user.date_register || user.DATE_REGISTER || Date.now()).toLocaleDateString()}</p>
                                    {updateSuccess && (
                                        <div className="update-success-inline" style={{ color: '#00c853', fontWeight: 'bold', marginBottom: '10px' }}>
                                            ✓ Профиль обновлен!
                                        </div>
                                    )}
                                    <button className="btn-edit-profile" onClick={() => setIsEditing(true)}>Редактировать профиль</button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="profile-content">
                <div className="container">
                    <div className="profile-tabs">
                        <button
                            className={`profile-tab ${activeTab === 'ads' ? 'active' : ''}`}
                            onClick={() => setActiveTab('ads')}
                        >
                            Объявления
                        </button>
                        <button
                            className={`profile-tab ${activeTab === 'reviews' ? 'active' : ''}`}
                            onClick={() => setActiveTab('reviews')}
                        >
                            Отзывы
                        </button>
                    </div>

                    <div className="tab-panel">
                        {activeTab === 'ads' && (
                            <div className="ads-tab">
                                <div className="tab-header">
                                    <h3>Мои объявления</h3>
                                    <button className="btn-primary" onClick={() => setIsModalOpen(true)}>Создать объявление</button>
                                </div>

                                {myJobs.length > 0 ? (
                                    <div className="jobs-list">
                                        {myJobs.map(job => (
                                            <div key={job.ID} className="profile-job-card">
                                                {job.PREVIEW_PICTURE_SRC && (
                                                    <div className="job-image">
                                                        <img src={getImageUrl(job.PREVIEW_PICTURE_SRC)} alt={job.NAME} />
                                                    </div>
                                                )}
                                                <div className="job-info">
                                                    <h4>{job.NAME}</h4>
                                                    <p className="job-desc" dangerouslySetInnerHTML={{ __html: cleanText(job.PREVIEW_TEXT) }} />
                                                    {job.PROPERTIES?.PRICE?.VALUE && <p className="job-price">{job.PROPERTIES.PRICE.VALUE}</p>}
                                                    <div className="job-actions-profile">
                                                        <Link to={`/work/${job.CODE}`} className="btn-view">Просмотреть</Link>
                                                        <button
                                                            onClick={() => handleDeleteJob(job.ID)}
                                                            className="btn-delete"
                                                            title="Удалить"
                                                        >
                                                            Удалить
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="empty-state">
                                        <img src="/local/templates/kapussta/img/doll.svg" alt="empty" />
                                        <p>У вас пока нет активных объявлений</p>
                                        <button className="btn-primary" onClick={() => setIsModalOpen(true)}>Создать первое объявление</button>
                                    </div>
                                )}
                            </div>
                        )}
                        {activeTab === 'reviews' && (
                            <div className="reviews-tab">
                                <div className="tab-header" style={{ marginBottom: '20px' }}>
                                    <h3>Отзывы ({reviews.length})</h3>
                                </div>

                                {loadingReviews ? (
                                    <div className="loading" style={{ padding: '20px', textAlign: 'center' }}>Загрузка отзывов...</div>
                                ) : reviews.length > 0 ? (
                                    <div className="reviews-list" style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '40px' }}>
                                        {reviews.map(review => (
                                            <div key={review.id} className="review-card" style={{ background: '#f9f9f9', padding: '15px', borderRadius: '10px', color: '#111' }}>
                                                <div className="review-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                                    <div className="reviewer-info" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <div className="reviewer-avatar" style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#eee', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold' }}>
                                                            {review.author_avatar_src ? (
                                                                <img src={getImageUrl(review.author_avatar_src)} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                            ) : (
                                                                (review.author_name || review.author_login || '?').charAt(0).toUpperCase()
                                                            )}
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                            <Link to={`/profile/${review.author_id}`} className="reviewer-name" style={{ fontWeight: 'bold', color: '#8e44ad', textDecoration: 'none' }}>
                                                                {review.author_name || review.author_login}
                                                            </Link>
                                                            <StarRating rating={review.rating} />
                                                        </div>
                                                    </div>
                                                    <span className="review-date" style={{ fontSize: '12px', color: '#888' }}>
                                                        {new Date(review.date_create).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                <p className="review-text" style={{ margin: 0, lineHeight: '1.4' }}>{review.text}</p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="empty-state">
                                        <p>У вас пока нет отзывов</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>Создать объявление</h2>
                            <button className="close-modal" onClick={() => setIsModalOpen(false)}>×</button>
                        </div>
                        <form onSubmit={handleAdSubmit}>
                            <div className="form-group">
                                <label>Название *</label>
                                <input type="text" required value={newAd.name} onChange={e => setNewAd({ ...newAd, name: e.target.value })} />
                            </div>
                            <div className="form-row">
                                <div className="form-group custom-select-container">
                                    <label>Страна (можно выбрать несколько) *</label>
                                    <div
                                        className="custom-select-trigger"
                                        onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
                                    >
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {(() => {
                                                if (newAd.country.length === 0) return 'Выберите страны';

                                                const selectedNames = countries
                                                    .filter(c => newAd.country.includes(c.CODE_COUNTRY))
                                                    .map(c => c.NAME);

                                                if (selectedNames.length <= 2) {
                                                    return selectedNames.join(', ');
                                                } else {
                                                    return `${selectedNames[0]}, ${selectedNames[1]} +${selectedNames.length - 2}`;
                                                }
                                            })()}
                                        </span>
                                        <span style={{ fontSize: '12px', marginLeft: '10px' }}>▼</span>
                                    </div>

                                    {isCountryDropdownOpen && (
                                        <div className="custom-select-options custom-scrollbar">
                                            {countries.map(c => (
                                                <div
                                                    key={c.ID}
                                                    className="option-item"
                                                    onClick={() => {
                                                        const selectedCodes = newAd.country.includes(c.CODE_COUNTRY)
                                                            ? newAd.country.filter(code => code !== c.CODE_COUNTRY)
                                                            : [...newAd.country, c.CODE_COUNTRY];
                                                        setNewAd({ ...newAd, country: selectedCodes });
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={newAd.country.includes(c.CODE_COUNTRY)}
                                                        readOnly
                                                    />
                                                    <span>{c.NAME}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {isCountryDropdownOpen && (
                                        <div
                                            className="dropdown-overlay"
                                            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }}
                                            onClick={() => setIsCountryDropdownOpen(false)}
                                        />
                                    )}
                                </div>
                                <div className="form-group">
                                    <label>Тип</label>
                                    <select required value={newAd.type} onChange={e => setNewAd({ ...newAd, type: e.target.value })}>
                                        <option value="Агентства">Агентства</option>
                                        <option value="Модели">Модели</option>
                                        <option value="Операторы">Операторы</option>
                                        <option value="Скауты">Скауты</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Ищу / Предлагаю *</label>
                                <select required value={newAd.ad_type} onChange={e => setNewAd({ ...newAd, ad_type: e.target.value })}>
                                    <option value="OFFERING">Предлагаю</option>
                                    <option value="SEEKING">Ищу</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Цена</label>
                                <input type="text" value={newAd.price} onChange={e => setNewAd({ ...newAd, price: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <label>Краткое описание (макс. 100 символов)</label>
                                    <span style={{ fontSize: '12px', color: newAd.preview_text.length >= 100 ? '#e74c3c' : '#777' }}>
                                        {newAd.preview_text.length}/100
                                    </span>
                                </div>
                                <textarea
                                    rows="2"
                                    maxLength={100}
                                    value={newAd.preview_text}
                                    onChange={e => setNewAd({ ...newAd, preview_text: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Полное описание</label>
                                <textarea rows="4" value={newAd.detail_text} onChange={e => setNewAd({ ...newAd, detail_text: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Изображение</label>
                                <input type="file" onChange={e => setNewAd({ ...newAd, image: e.target.files[0] })} />
                            </div>
                            <button type="submit" className="btn-submit-ad">Опубликовать</button>
                        </form>
                    </div>
                </div>
            )}

            {isSuccessModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="modal-content success-modal" style={{ textAlign: 'center', padding: '40px 20px', maxWidth: '400px', borderRadius: '24px', background: '#1a1a1a', border: '1px solid #333' }}>
                        <div style={{ fontSize: '60px', marginBottom: '20px', color: '#836FFF' }}>✓</div>
                        <h2 style={{ marginBottom: '10px', color: '#fff' }}>Успешно!</h2>
                        <p style={{ color: '#aaa', fontSize: '16px' }}>Объявление создано!</p>
                        <button
                            className="btn-primary"
                            style={{ marginTop: '20px', width: '100%', padding: '12px', borderRadius: '12px', background: '#836FFF', border: 'none', color: '#fff', fontSize: '16px', fontWeight: '600' }}
                            onClick={() => setIsSuccessModalOpen(false)}
                        >
                            Отлично
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Profile;
